import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { EmberInvasionCandidate, EntityId, ScreenDimensions, TileSize } from "game-common/models"
import { TextRole } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"

// The invasion handshake's two client-side controls, which is why they share a file: one picks a
// camp to rise in, the other lets go of the body once you are wearing it. See
// docs/ember-the-fallen-design.md ("Pick" and "Leave") and run_summary_format.ts, which owns the
// rule about whether the offer is on the scorecard at all.
//
// THE PICKER: up to three cards -- a defending player's name, the wave their camp is on, and how
// soon the next one lands -- opened from "Resurrect as Undead!" on the end-of-run panel and drawn
// over it. Picking a card sends `join`; backing out sends `cancel`.
//
// It opens with NO round trip. Protocol.type_updateInvasionOffer is an interval FEED (~2s), so the
// client already holds the list when the button is pressed; the `seek` that goes out with the press
// is a nudge for a fresher one, never something the picker waits on.
//
// Two consequences of being fed rather than asked, both of which this widget is shaped around:
//
//   1. A CARD CAN VANISH WHILE IT IS BEING READ, and a press would then land on whatever slid into
//      its place -- invading a stranger the player never chose. So the slots are FIXED and ORDERED
//      underneath (a camp keeps the slot it was first given, and one it vacates is never handed to
//      anybody else), while what is DRAWN closes up and re-centres around the live ones. The first
//      version left the dead slot standing and marked; on a quiet server that meant mostly looking
//      at gaps. A re-centre is held back while the row is hovered, which is what keeps the press
//      honest: the hazard is a card moving while a player is reaching for it, since the press
//      resolves on pointerDOWN.
//   2. `secondsToWave` IS STALE THE INSTANT IT IS SENT. So it is drained locally between feed
//      ticks and re-seated by each one -- the same trick the invitation prompt uses on its own
//      deadline (see ember_invite_prompt.ts), and for the same reason: a number that only moved
//      every two seconds would read as a stopped clock, and asking the server for each tick is a
//      message a frame can answer for itself.
//
// Styled in the same ember-lit language as the role picker and the scorecard (warm amber rim, dark
// glass, fake glows rather than blur filters -- filters are expensive on weak GPUs) because it is
// opened from the scorecard and has to read as the same set.

// ===== pure helpers ===================================================================
// Everything a card SAYS is here, as functions of a candidate and the age of the snapshot it came
// in on. Tested in __tests__/invasion_picker.test.ts: the labels are the interesting part of this
// widget, and they are also the part that has to be honest.

// Who the camp belongs to. A candidate with no name is not an error -- the server sends the
// defending player's entity either way -- so it gets the fiction's word for a nameless camp rather
// than the roster's "???".
export const NAMELESS = "A stranger"
// Names are truncated by CHARACTER count, not measured width: pixi's text metrics need a real
// canvas, so a width rule behaves differently under a test runner than it does in the game.
export const NAME_MAX_CHARS = 14

export const candidateName = (candidate?: { name?: string }): string => {
    const name = candidate?.name?.trim()
    if (!name) {
        return NAMELESS
    }
    return name.length > NAME_MAX_CHARS ? name.slice(0, NAME_MAX_CHARS - 1) + "…" : name
}

// Which wave that camp is on. `wave: 0` means its first wave has not landed yet, which is a
// different thing from being between waves and is worth saying.
export const candidateWaveLabel = (candidate: EmberInvasionCandidate): string => {
    const wave = Math.max(0, Math.floor(candidate?.wave || 0))
    return wave > 0 ? `WAVE ${wave}` : "NO WAVE YET"
}

// How many seconds the countdown has left, drained by however long ago the snapshot arrived.
// Undefined when there is no countdown to report (a wave is already on the field, or the camp
// isn't counting).
export const secondsLeft = (candidate: EmberInvasionCandidate, ageMs: number = 0): number | undefined => {
    const seconds = candidate?.secondsToWave
    if (typeof seconds !== "number" || !Number.isFinite(seconds)) {
        return undefined
    }
    return Math.max(0, Math.ceil(Math.max(0, seconds) - Math.max(0, ageMs) / 1000))
}

// What the card says about timing. Honesty is the whole point of this line: the design rejected a
// "only camps with 20s of quiet left" gate because it would fire about one death in six, and the
// price of dropping it is that the picker has to say what it is sending the player into.
//
// A countdown that has run down to zero reads as LANDING rather than as "in 0s": the feed is two
// seconds coarse, so zero means "the wave is arriving and we have not been told yet".
export const candidateTimingLabel = (candidate: EmberInvasionCandidate, ageMs: number = 0): string => {
    if (candidate?.waveActive) {
        return "fighting now"
    }
    const left = secondsLeft(candidate, ageMs)
    if (left === undefined) {
        return "quiet for now"
    }
    return left > 0 ? `in ${left}s` : "landing now"
}

// ===== geometry =======================================================================
// The stage stretches everything vertically by 1.3, so these are stage units with that already
// accounted for -- the same frame run_summary.ts works in, because this opens on top of it.
const SCREEN_W = ScreenDimensions.w + TileSize * 4.7
const CENTER_X = SCREEN_W / 2
const CENTER_Y = ScreenDimensions.h / 2 + 52

// Three slots, always. See the note above: fixed slots are what stop a card moving under a pointer.
export const SLOTS = 3

const PAD = 16
const TITLE_H = 42
export const CARD_W = 118
const CARD_H = 104
export const CARD_GAP = 10
const FOOTER_H = 28
const FOOTER_GAP = 12

const PANEL_W = PAD * 2 + SLOTS * CARD_W + (SLOTS - 1) * CARD_GAP
const PANEL_H = TITLE_H + CARD_H + FOOTER_GAP + FOOTER_H + PAD
const PANEL_LEFT = CENTER_X - PANEL_W / 2
const PANEL_TOP = CENTER_Y - PANEL_H / 2
const CARD_TOP = PANEL_TOP + TITLE_H
const FOOTER_TOP = CARD_TOP + CARD_H + FOOTER_GAP
const FOOTER_W = 150

// Palette, shared with run_summary.ts / role_picker.ts.
const RIM = 0xf2a94e
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.97
const CARD_FILL = 0x241b16
const CARD_HOVER_FILL = 0x33271e
const CARD_RIM = 0x6b4f38
const TEXT_COLOR = 0xfff3e0
const SCRIM_COLOR = 0x06060a
const MUTED = 0x8d97ad

// The undead's own colour, used only for the thing the player is about to become: a cold rim on a
// live card and on "Let go". Everything else on the panel is warm, so one cool accent is enough to
// say this way out is not the camp's.
const UNDEAD = 0x9fd2ff
// A camp already fighting is the louder choice, not the disqualified one.
const FIGHTING = 0xff8a5c

// Fade timing -- mirrors the role picker / scorecard (~100ms each way), done by hand here because
// this widget is shown and hidden by the GUI manager rather than by a Fader of its own.
const FADE_STEP = 0.14

export interface InvasionPickerProps {
    // A card was picked: rise in that defending player's camp.
    onJoin?: (entityId: EntityId) => void
    // Backed out -- the footer button, or a press on the scrim.
    onCancel?: () => void
}

// One of the three fixed slots.
interface SlotHandle {
    container: Container
    bg: Graphics
    name: Text
    wave: Text
    timing: Text
    // The camp this slot is showing, if any. Read at PRESS time rather than closed over, so a slot
    // whose camp changed under the player cannot send the id it used to hold.
    entityId?: EntityId
    // Held a camp that has since dropped out of the feed. Drawn dead, saying so, and never reused:
    // a slot that recycled itself would be the sliding row this design is avoiding.
    vacated: boolean
    hovered: boolean
}

export class InvasionPicker extends Container {
    private onJoin?: (entityId: EntityId) => void
    private onCancel?: () => void

    private scrim: Graphics
    private panel: Container
    private plate = new Graphics()
    private title: Text
    private subtitle: Text
    private slots: SlotHandle[] = []
    private cardRow = new Container()
    // The "no camp is open to the dead" state, which replaces the card row wholesale.
    private emptyLine: Text
    private emptySub: Text
    private footer = new Graphics()
    private footerLabel: Text

    // The newest list the feed gave us, and how long ago it arrived. The age is what drains the
    // countdowns; see candidateTimingLabel.
    private candidates: EmberInvasionCandidate[] = []
    private ageMs = 0
    // The timing strings last drawn, so the per-frame drain only touches a Text when its second
    // actually ticks over.
    private drawnTiming: string[] = []

    // The live cards' layout as last drawn, and whether a newer one is waiting on the pointer
    // leaving the row. See layoutCards.
    private laidOut?: string
    private layoutPending = false
    private opened = false

    constructor(props: InvasionPickerProps = {}) {
        super()
        this.onJoin = props.onJoin
        this.onCancel = props.onCancel

        // The scrim swallows every press that isn't on a card, and a press on it is an answer:
        // clicking off this panel means "not now". It has to swallow them even when it isn't
        // answering, or a stray click fires the player's bow into the camp behind the scorecard.
        this.scrim = new Graphics()
        this.scrim.beginFill(SCRIM_COLOR, 0.55)
        this.scrim.drawRect(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.endFill()
        this.scrim.interactive = true
        this.scrim.hitArea = new Rectangle(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.onCancel?.()
        })
        this.scrim.on("pointermove", (event: any) => event.stopPropagation())
        this.scrim.on("rightclick", (event: any) => event.stopPropagation())
        this.addChild(this.scrim)

        this.panel = new Container()
        // Presses inside the panel never reach the scrim, so the panel's own gaps are not a way to
        // dismiss it by accident.
        this.panel.interactive = true
        this.panel.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H)
        this.panel.on("pointerdown", (event: any) => event.stopPropagation())
        this.panel.on("pointermove", (event: any) => event.stopPropagation())
        this.addChild(this.panel)

        this.panel.addChild(this.plate)
        this.drawPlate()

        this.title = this.text("THE DEAD WALK", { role: "compactTitle", color: RIM, bold: true, center: true })
        this.title.y = PANEL_TOP + 10
        this.subtitle = this.text("Choose a fire to rise at. You will wake as one of its attackers.", {
            role: "compactCaption",
            alpha: 0.6,
            center: true,
        })
        this.subtitle.y = PANEL_TOP + 29
        this.panel.addChild(this.title, this.subtitle)

        this.cardRow.y = 0
        this.panel.addChild(this.cardRow)
        for (let i = 0; i < SLOTS; i++) {
            this.slots.push(this.buildSlot(i))
        }

        // Centred on the card row's own band, so the two states occupy the same space and the panel
        // does not appear to resize when the last camp drops out.
        this.emptyLine = this.text("No camp is open to the dead right now.", {
            role: "compactLabel",
            color: MUTED,
            center: true,
        })
        this.emptyLine.y = CARD_TOP + CARD_H / 2 - 16
        this.emptySub = this.text("The living are keeping their fires closed. Try again in a moment.", {
            role: "compactCaption",
            alpha: 0.5,
            center: true,
        })
        this.emptySub.y = CARD_TOP + CARD_H / 2 + 2
        this.panel.addChild(this.emptyLine, this.emptySub)

        this.footerLabel = this.text("NOT YET", { role: "compactLabel", color: TEXT_COLOR, bold: true, center: true })
        this.footer.x = CENTER_X - FOOTER_W / 2
        this.footer.y = FOOTER_TOP
        this.footerLabel.y = FOOTER_TOP + 8
        this.footer.interactive = true
        this.footer.cursor = "pointer"
        this.footer.hitArea = new Rectangle(-4, -4, FOOTER_W + 8, FOOTER_H + 8)
        this.footer.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.onCancel?.()
        })
        this.footer.on("pointerover", () => this.drawFooter(true))
        this.footer.on("pointerout", () => this.drawFooter(false))
        this.panel.addChild(this.footer, this.footerLabel)
        this.drawFooter(false)

        this.fill()
        this.visible = false
    }

    // ===== public API =================================================================

    public isOpen = (): boolean => this.opened

    // Open on what the client already holds. Deliberately takes the list rather than fetching one:
    // the press must not wait for a server round trip (see the note at the top of this file).
    //
    // The slots are wiped FIRST. Clearing them on close instead would be wrong in the other
    // direction: a slot left holding a camp that has since dropped out would come back up as a gap
    // in a row, sized for a camp the player never saw.
    public open = (candidates: EmberInvasionCandidate[]) => {
        this.slots.forEach(slot => {
            slot.entityId = undefined
            slot.vacated = false
            slot.hovered = false
        })
        // Nothing is hovered on a picker that is not up yet, so there is nothing to defer to -- and
        // the last visit's positions must not be inherited.
        this.laidOut = undefined
        this.layoutPending = false
        this.setCandidates(candidates)
        this.layoutCards(true)
        if (!this.visible) {
            this.alpha = 0
        }
        this.opened = true
        this.visible = true
    }

    // Dismiss. The content is deliberately left standing so the fade-out shows what the player was
    // looking at rather than collapsing to the empty state on its way off the screen.
    public close = () => {
        this.opened = false
        this.slots.forEach(slot => {
            if (slot.hovered) {
                slot.hovered = false
                this.drawSlot(slot)
            }
        })
    }

    // A fresh list off the feed. No-op on the display tree while closed, but the candidates are
    // still remembered: the scorecard's button reads them, and the picker may be opened from them
    // on the very next press.
    public setCandidates = (candidates: EmberInvasionCandidate[]) => {
        this.candidates = candidates || []
        this.ageMs = 0
        this.seatCandidates()
        this.fill()
    }

    // Which camps the picker currently has to offer -- what a card press can mean, for a caller
    // outside the display tree (and for a test).
    public liveEntityIds = (): EntityId[] =>
        this.slots.filter(slot => !!slot.entityId && !slot.vacated).map(slot => slot.entityId!)

    // Whether the panel is showing the "no camp is open" state rather than any cards.
    public isEmptyState = (): boolean => this.liveEntityIds().length < 1

    // What one slot currently says, top line to bottom. The readable summary of a slot for a caller
    // outside the display tree.
    public slotText = (index: number): string[] => {
        const slot = this.slots[index]
        return slot ? [slot.name.text, slot.wave.text, slot.timing.text] : []
    }

    // Pick a camp. This IS the card's handler, and it is public because the cards are buried in the
    // display tree -- anything holding a defending player's id holds the control.
    public pick = (entityId: EntityId) => {
        // The `opened` guard is for the fade-out: the panel is still drawn (and still catching
        // presses) for ~100ms after it has been dismissed, and a camp invaded on the way out would
        // be a camp nobody chose.
        if (!this.opened) {
            return
        }
        const slot = this.slots.find(s => s.entityId === entityId && !s.vacated)
        if (!slot) {
            return
        }
        this.onJoin?.(entityId)
    }

    // Drain the countdowns, and the fade. Called every frame by the GUI manager; cheap while shut.
    public animate = (dtMs: number) => {
        if (!this.visible) {
            return
        }
        const target = this.opened ? 1 : 0
        if (this.alpha !== target) {
            this.alpha =
                target > this.alpha ? Math.min(1, this.alpha + FADE_STEP) : Math.max(0, this.alpha - FADE_STEP)
            if (this.alpha <= 0) {
                this.visible = false
                return
            }
        }
        if (!this.opened) {
            return
        }
        this.ageMs += dtMs
        this.refreshTiming()
    }

    // ===== internals ==================================================================

    // Seat the newest list in the fixed slots: a camp keeps the slot it already had, a camp that has
    // dropped out leaves its slot standing (dead), and only a slot that never held anything takes a
    // newcomer. That is the whole anti-slide rule -- see the file header.
    private seatCandidates = () => {
        const byId = new Map<EntityId, EmberInvasionCandidate>()
        this.candidates.slice(0, SLOTS).forEach(candidate => {
            if (candidate?.entityId) {
                byId.set(candidate.entityId, candidate)
            }
        })

        this.slots.forEach(slot => {
            if (!slot.entityId) {
                return
            }
            if (byId.has(slot.entityId)) {
                byId.delete(slot.entityId)
                slot.vacated = false
                return
            }
            // Gone. Keep the slot, mark it, and never hand it to anybody else.
            slot.vacated = true
        })

        Array.from(byId.keys()).forEach(entityId => {
            const free = this.slots.find(slot => !slot.entityId)
            if (!free) {
                return
            }
            free.entityId = entityId
            free.vacated = false
        })
    }

    private candidateFor = (entityId?: EntityId): EmberInvasionCandidate | undefined =>
        entityId ? this.candidates.find(candidate => candidate?.entityId === entityId) : undefined

    // Write every slot's labels and redraw it. Called on each feed tick (and on open), not per
    // frame -- the per-frame path is refreshTiming, which touches one line.
    private fill = () => {
        const empty = this.isEmptyState()
        this.cardRow.visible = !empty
        this.emptyLine.visible = empty
        this.emptySub.visible = empty
        this.title.text = empty ? "THE DEAD WAIT" : "THE DEAD WALK"
        this.subtitle.text = empty
            ? "Nothing is open to you yet."
            : "Choose a fire to rise at. You will wake as one of its attackers."
        this.footerLabel.text = empty ? "BACK" : "NOT YET"
        this.center(this.title)
        this.center(this.subtitle)
        this.center(this.emptyLine)
        this.center(this.emptySub)
        this.footerLabel.x = Math.round(CENTER_X - this.footerLabel.width / 2)

        this.slots.forEach((slot, index) => {
            const candidate = this.candidateFor(slot.entityId)
            const live = !!candidate && !slot.vacated
            slot.container.interactive = live
            slot.container.cursor = live ? "pointer" : "default"
            if (live) {
                slot.name.text = candidateName(candidate)
                slot.wave.text = candidateWaveLabel(candidate!)
                slot.timing.text = candidateTimingLabel(candidate!, this.ageMs)
                slot.timing.tint = candidate!.waveActive ? FIGHTING : UNDEAD
            } else {
                slot.name.text = ""
                slot.wave.text = ""
                slot.timing.text = ""
            }
            this.drawnTiming[index] = slot.timing.text
            this.centerIn(slot.name, CARD_W)
            this.centerIn(slot.wave, CARD_W)
            this.centerIn(slot.timing, CARD_W)
            this.drawSlot(slot)
        })

        this.layoutCards()
    }

    /** The slots currently showing a camp, in slot order. */
    private liveSlots = (): SlotHandle[] =>
        this.slots.filter(slot => !!this.candidateFor(slot.entityId) && !slot.vacated)

    /**
     * Place the live cards CENTRED, and draw nothing where a camp has dropped out.
     *
     * The slots themselves stay fixed and ordered -- a camp keeps the slot it was first given, and a
     * slot it has vacated is never handed to anybody else -- so what moves here is only where a card
     * is DRAWN, never which camp a given card means. That is what keeps "the press means the camp
     * the player was looking at" true while still closing the gaps up.
     *
     * Deferred while the pointer is over the row, because the press resolves on pointerDOWN: the
     * hazard is not a card moving mid-click, it is a card moving while the player is reaching for
     * it, which would land their press on a stranger they never chose. The pending layout is taken
     * the moment they move off.
     */
    private layoutCards = (force = false) => {
        const live = this.liveSlots()
        const signature = live.map(slot => slot.entityId).join("|")
        if (signature === this.laidOut) {
            this.layoutPending = false
            return
        }
        if (!force && this.slots.some(slot => slot.hovered)) {
            this.layoutPending = true
            return
        }

        const width = live.length * CARD_W + Math.max(0, live.length - 1) * CARD_GAP
        let x = Math.round(CENTER_X - width / 2)
        this.slots.forEach(slot => {
            slot.container.visible = false
        })
        live.forEach(slot => {
            slot.container.visible = true
            slot.container.x = x
            x += CARD_W + CARD_GAP
        })

        this.laidOut = signature
        this.layoutPending = false
    }

    /** Where the live cards sit, for a caller outside the display tree. */
    public cardPositions = (): number[] => this.liveSlots().map(slot => slot.container.x)

    /** Hovering a card, and the one thing it owes the layout on the way out. */
    private hover = (handle: SlotHandle, hovered: boolean) => {
        handle.hovered = hovered
        this.drawSlot(handle)
        // Whatever the feed said while they were reading it.
        if (!hovered && this.layoutPending && !this.slots.some(slot => slot.hovered)) {
            this.layoutCards()
        }
    }

    /** Drive the hover from a test: pixi's own pointer events are not available under jsdom. */
    public setHovered = (index: number, hovered: boolean) => {
        const slot = this.slots[index]
        if (slot) {
            this.hover(slot, hovered)
        }
    }

    // The per-frame half of the drain: only the timing line can change between feed ticks, and only
    // when its whole second ticks over, so this is a string compare per card and nothing else.
    private refreshTiming = () => {
        this.slots.forEach((slot, index) => {
            const candidate = this.candidateFor(slot.entityId)
            if (!candidate || slot.vacated) {
                return
            }
            const next = candidateTimingLabel(candidate, this.ageMs)
            if (next === this.drawnTiming[index]) {
                return
            }
            this.drawnTiming[index] = next
            slot.timing.text = next
            this.centerIn(slot.timing, CARD_W)
        })
    }

    private buildSlot = (index: number): SlotHandle => {
        const container = new Container()
        container.x = PANEL_LEFT + PAD + index * (CARD_W + CARD_GAP)
        container.y = CARD_TOP
        container.hitArea = new Rectangle(0, 0, CARD_W, CARD_H)

        const bg = new Graphics()
        container.addChild(bg)

        // A skull rather than a portrait: the client has no appearance for a player in another
        // room, and a card promising a face it cannot draw is worse than one that doesn't.
        const sigil = this.text("💀", { role: "title" })
        sigil.y = 12
        this.centerIn(sigil, CARD_W)
        container.addChild(sigil)

        const name = this.text("", { role: "compactBody", color: TEXT_COLOR, bold: true })
        name.y = 46
        const wave = this.text("", { role: "compactCaption", color: RIM })
        wave.y = 66
        const timing = this.text("", { role: "compactCaption", color: UNDEAD })
        timing.y = 80
        container.addChild(name, wave, timing)

        const handle: SlotHandle = { container, bg, name, wave, timing, vacated: false, hovered: false }

        container.on("pointerover", () => this.hover(handle, true))
        container.on("pointerout", () => this.hover(handle, false))
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            // Read the id off the slot at press time: the feed may have changed what this card is
            // showing since it was drawn, and the press has to mean whatever it says NOW.
            if (handle.entityId && !handle.vacated) {
                this.pick(handle.entityId)
            }
        })

        this.cardRow.addChild(container)
        return handle
    }

    private drawSlot = (slot: SlotHandle) => {
        const candidate = this.candidateFor(slot.entityId)
        const live = !!candidate && !slot.vacated
        const accent = candidate?.waveActive ? FIGHTING : UNDEAD
        const g = slot.bg
        g.clear()
        if (!live) {
            // An empty or spent slot is a frame, not a button: no glow, no fill to speak of.
            g.lineStyle(1, CARD_RIM, slot.vacated ? 0.5 : 0.25)
            g.beginFill(CARD_FILL, slot.vacated ? 0.5 : 0.25)
            g.drawRoundedRect(0, 0, CARD_W, CARD_H, 8)
            g.endFill()
            slot.name.alpha = 0.5
            slot.wave.alpha = 0.45
            slot.timing.alpha = 0.45
            return
        }
        slot.name.alpha = 1
        slot.wave.alpha = 0.85
        slot.timing.alpha = 0.95
        if (slot.hovered) {
            // Fake glow: a fatter low-alpha copy behind the card. Cheaper than a blur filter, which
            // is the rule across this UI because filters cost real money on a weak GPU.
            g.beginFill(accent, 0.18)
            g.drawRoundedRect(-3, -3, CARD_W + 6, CARD_H + 6, 11)
            g.endFill()
            g.lineStyle(2, accent, 1)
            g.beginFill(CARD_HOVER_FILL, 0.97)
        } else {
            g.lineStyle(1.5, CARD_RIM, 0.75)
            g.beginFill(CARD_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, CARD_W, CARD_H, 8)
        g.endFill()
    }

    private drawPlate = () => {
        const g = this.plate
        g.clear()
        g.beginFill(RIM, 0.06)
        g.drawRoundedRect(PANEL_LEFT - 6, PANEL_TOP - 6, PANEL_W + 12, PANEL_H + 12, 16)
        g.endFill()
        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H, 12)
        g.endFill()
    }

    private drawFooter = (hovered: boolean) => {
        const g = this.footer
        g.clear()
        g.lineStyle(1.5, hovered ? RIM : CARD_RIM, hovered ? 1 : 0.7)
        g.beginFill(CARD_FILL, hovered ? 0.95 : 0.8)
        g.drawRoundedRect(0, 0, FOOTER_W, FOOTER_H, 7)
        g.endFill()
    }

    private text = (
        value: string,
        opts: { role: TextRole; color?: number; alpha?: number; bold?: boolean; center?: boolean },
    ): Text => {
        let renderer = new TextRenderer().role(opts.role).bold(false).align("center")
        if (opts.color !== undefined) {
            renderer = renderer.color(opts.color)
        }
        if (opts.bold) {
            renderer = renderer.bold()
        }
        const text = renderer.render(value) as Text
        if (opts.alpha !== undefined) {
            text.alpha = opts.alpha
        }
        if (opts.center) {
            this.center(text)
        }
        return text
    }

    // Centre on the panel's own axis. Both of these are no-ops under a mocked canvas (every Text
    // measures as zero wide there), which is why no test asserts on a position.
    private center = (text: Text) => {
        text.x = Math.round(CENTER_X - text.width / 2)
    }

    private centerIn = (text: Text, width: number) => {
        text.x = Math.round(width / 2 - text.width / 2)
    }
}

// ===== "Let go" =======================================================================

// LEAVING: the one exit the invader presses themself. The other four fire on their own (you die,
// you kill the Lord, the wave is cleared, the defender leaves), and all five land in the same
// place: a fresh camp of the player's own.
//
// It exists ONLY while Message.invading is true, which is why this is a button and not a menu item:
// a player wearing somebody else's attacker, in a room that is not theirs, needs exactly one
// visible way home and needs to find it without opening anything.
//
// Cool-rimmed where the camp HUD is warm, because it belongs to the invasion rather than to the
// camp being invaded.
const LETGO_W = 104
const LETGO_H = 26

export class LetGoButton extends Container {
    private bg = new Graphics()
    private label: Text
    private hovered = false

    constructor({ onPress }: { onPress: () => void }) {
        super()

        this.label = new TextRenderer().role("compactLabel").color(TEXT_COLOR).bold().align("center").render("LET GO") as Text
        this.label.x = Math.round(LETGO_W / 2 - this.label.width / 2)
        this.label.y = 6

        this.addChild(this.bg, this.label)
        this.interactive = true
        this.cursor = "pointer"
        // Proud of the fill on every side: it is 26px tall and it is the way out.
        this.hitArea = new Rectangle(-4, -4, LETGO_W + 8, LETGO_H + 8)
        this.on("pointerdown", (event: any) => {
            event.stopPropagation()
            onPress()
        })
        this.on("pointerover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.draw()
        })

        this.draw()
        this.visible = false
    }

    public getWidth = (): number => LETGO_W
    public getHeight = (): number => LETGO_H

    // Follows the server's verdict on every message (Message.invading), exactly as the downed flag
    // does -- never a local guess, so the button cannot outlive the body it lets go of.
    public setInvading = (invading: boolean) => {
        if (this.visible === invading) {
            return
        }
        this.visible = invading
        this.hovered = false
        this.draw()
    }

    private draw = () => {
        const g = this.bg
        g.clear()
        g.beginFill(UNDEAD, this.hovered ? 0.2 : 0.1)
        g.drawRoundedRect(-3, -3, LETGO_W + 6, LETGO_H + 6, 9)
        g.endFill()
        g.lineStyle(1.5, UNDEAD, this.hovered ? 1 : 0.75)
        g.beginFill(0x0a1118, this.hovered ? 0.96 : 0.86)
        g.drawRoundedRect(0, 0, LETGO_W, LETGO_H, 6)
        g.endFill()
        this.label.alpha = this.hovered ? 1 : 0.9
    }
}
